import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { AuthSkyline } from '../components/common/AuthSkyline';
import { NAVIGATION_HEIGHT } from '../constants/global';

const TermsOfService = () => (
  <>
    <Helmet>
      <title>Terms of Service - tute</title>
      <meta name="description" content="Terms of service for tute.academy — Oxford & Cambridge tutoring platform." />
    </Helmet>
    <div style={{ position: 'relative', marginTop: NAVIGATION_HEIGHT }}>
      <AuthSkyline extraLight fixed />
      <div style={{ maxWidth: '720px', margin: '0 auto', padding: '2rem 1.5rem 4rem', position: 'relative', zIndex: 1 }}>

      <h1 style={{ fontSize: '2rem', fontWeight: 700, color: '#0f172a', margin: '0 0 0.5rem' }}>Terms of Service</h1>
      <p style={{ color: '#64748b', fontSize: '0.9rem', margin: '0 0 2rem' }}>Last updated: 12 April 2026</p>

      <Section title="1. About tute">
        tute is a platform operated at <strong>tute.academy</strong> that connects parents seeking tutoring for their children with verified tutors from the University of Oxford and the University of Cambridge. By creating an account, you agree to these terms.
      </Section>

      <Section title="2. Eligibility">
        <ul style={{ paddingLeft: '1.25rem', margin: '0.5rem 0' }}>
          <li><strong>Tutors</strong> must be current students or recent graduates of the University of Oxford or the University of Cambridge. All tutor accounts are subject to verification before becoming visible on the platform.</li>
          <li><strong>Parents</strong> must be at least 18 years of age.</li>
        </ul>
      </Section>

      <Section title="3. Accounts">
        You are responsible for maintaining the confidentiality of your login credentials. You must provide accurate information when registering. We reserve the right to suspend or terminate accounts that violate these terms or provide false information.
      </Section>

      <Section title="4. Tutor verification">
        All tutors undergo a verification process. We check university affiliation, college, and degree information. Tutors are not visible to parents until they have been verified by a tute administrator. Verification does not constitute an endorsement of teaching quality.
      </Section>

      <Section title="5. Lessons and booking">
        <ul style={{ paddingLeft: '1.25rem', margin: '0.5rem 0' }}>
          <li>Lessons are booked through the platform at prices set by the tutor.</li>
          <li>Payment is collected at the time of booking via Stripe.</li>
          <li>All prices are displayed in GBP.</li>
          <li>Lesson events may be synced to Google Calendar if the tutor has connected their account.</li>
        </ul>
      </Section>

      <Section title="6. Cancellations and refunds">
        <ul style={{ paddingLeft: '1.25rem', margin: '0.5rem 0' }}>
          <li>Lessons cancelled before they are due to start will receive a full refund.</li>
          <li>Lessons that have already taken place cannot be refunded.</li>
          <li>Refunds are processed back to the original payment method via Stripe.</li>
        </ul>
      </Section>

      <Section title="7. Conduct">
        All users are expected to behave professionally and respectfully. tute reserves the right to remove any user who:
        <ul style={{ paddingLeft: '1.25rem', margin: '0.5rem 0' }}>
          <li>Engages in abusive, threatening, or inappropriate behaviour.</li>
          <li>Provides false credentials or information.</li>
          <li>Attempts to circumvent the platform for payments.</li>
        </ul>
      </Section>

      <Section title="8. Intellectual property">
        The tute name, logo, and website design are the property of tute. You may not reproduce, distribute, or create derivative works from any part of the platform without our written consent.
      </Section>

      <Section title="9. Limitation of liability">
        tute is a platform that facilitates connections between tutors and parents. We are not a party to the tutoring arrangement itself. We do not guarantee the quality of tutoring, academic outcomes, or the accuracy of tutor profiles beyond our verification process. To the fullest extent permitted by law, tute is not liable for any indirect, incidental, or consequential damages arising from the use of the platform.
      </Section>

      <Section title="10. Google Calendar integration">
        If you connect your Google Calendar, you authorise tute to create calendar events for booked lessons and check calendar availability. You can revoke this access at any time from your profile settings. Our use of Google Calendar data is governed by our <Link to="/privacy" style={{ color: '#2563eb' }}>Privacy Policy</Link>.
      </Section>

      <Section title="11. Privacy">
        Your use of tute is also governed by our <Link to="/privacy" style={{ color: '#2563eb' }}>Privacy Policy</Link>, which explains how we collect, use, and protect your data.
      </Section>

      <Section title="12. Changes to these terms">
        We may update these terms from time to time. Continued use of the platform after changes constitutes acceptance of the updated terms. We will notify registered users of significant changes via email.
      </Section>

      <Section title="13. Governing law">
        These terms are governed by the laws of England and Wales.
      </Section>

      <Section title="14. Contact">
        For questions about these terms, contact us at <strong>patrickborman16@gmail.com</strong>.
      </Section>
    </div>
    </div>
  </>
);

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div style={{ marginBottom: '2rem' }}>
    <h2 style={{ fontSize: '1.15rem', fontWeight: 600, color: '#0f172a', margin: '0 0 0.5rem' }}>{title}</h2>
    <div style={{ color: '#334155', fontSize: '0.95rem', lineHeight: 1.7 }}>{children}</div>
  </div>
);

export default TermsOfService;
